import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Folder as FolderIcon,
  MoreVert as MoreIcon,
  Label as LabelIcon,
  Circle as ColorIcon
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';

// Components
import FilterBar from '../core/FilterBar';

// Stores
import usePDFStore from '../../stores/pdfStore';
import useNotificationStore from '../../stores/notificationStore';

const PDFCategories = () => {
  const theme = useTheme();
  const { categories, loading, fetchCategories, createCategory, updateCategory, deleteCategory } = usePDFStore();
  const { showSuccess, showError } = useNotificationStore();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    color: '#2196F3',
    icon: 'folder'
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  
  // Load categories
  useEffect(() => {
    // Fetch categories when component mounts
    // The error handling in the store will prevent auth errors from showing
    const loadCategories = async () => {
      try {
        await fetchCategories();
      } catch (error) {
        // Error already handled in store
        console.error('Error loading categories:', error);
      }
    };
    loadCategories();
  }, [fetchCategories]);
  
  // Filter categories
  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cat.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Handle search change
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };
  
  // Handle clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };
  
  // Handle dialog open
  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || '',
        color: category.color || '#2196F3',
        icon: category.icon || 'folder'
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        color: '#2196F3',
        icon: 'folder'
      });
    }
    setDialogOpen(true);
  };
  
  // Handle save
  const handleSave = async () => {
    if (!formData.name.trim()) {
      showError('Category name is required');
      return;
    }
    
    try {
      if (editingCategory) {
        await updateCategory(editingCategory._id, formData);
        showSuccess('Category updated');
      } else {
        await createCategory(formData);
        showSuccess('Category created');
      }
      
      setDialogOpen(false);
      fetchCategories();
    } catch (error) {
      showError(error.message || 'Failed to save category');
    }
  };
  
  // Handle delete
  const handleDelete = async () => {
    if (!selectedCategory) return;
    
    try {
      await deleteCategory(selectedCategory._id);
      showSuccess('Category deleted');
      setDeleteConfirmOpen(false);
      setSelectedCategory(null);
      fetchCategories();
    } catch (error) {
      showError(error.message || 'Failed to delete category');
    }
  };
  
  // Handle menu
  const handleMenuOpen = (event, category) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedCategory(category);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Icon options
  const iconOptions = [
    { value: 'folder', label: 'Folder', icon: <FolderIcon /> },
    { value: 'label', label: 'Label', icon: <LabelIcon /> },
    { value: 'document', label: 'Document', icon: <FolderIcon /> },
    { value: 'education', label: 'Education', icon: <FolderIcon /> },
    { value: 'business', label: 'Business', icon: <FolderIcon /> },
    { value: 'personal', label: 'Personal', icon: <FolderIcon /> }
  ];
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight={500}>
          Categories
        </Typography>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          size="small"
          onClick={() => handleOpenDialog()}
        >
          New Category
        </Button>
      </Box>
      
      {/* Filter Bar */}
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        searchPlaceholder="Search categories..."
        hasActiveFilters={searchTerm !== ''}
        hideSearch={false}
      />
      
      {/* Categories List */}
      {categories.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', flex: 1, bgcolor: 'background.paper' }} elevation={0} variant="outlined">
          <Typography color="text.secondary" variant="body2">
            No categories yet. Create your first category to organize PDFs.
          </Typography>
        </Paper>
      ) : filteredCategories.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', flex: 1, bgcolor: 'background.paper' }} elevation={0} variant="outlined">
          <Typography color="text.secondary" variant="body2">
            No categories found matching "{searchTerm}"
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} variant="outlined" sx={{ flex: 1, overflow: 'auto', bgcolor: 'background.paper' }}>
          <List sx={{ p: 0 }}>
            {filteredCategories.map((category, index) => (
              <React.Fragment key={category._id}>
                {index > 0 && <Divider />}
                <ListItem
                  button
                  onClick={() => handleOpenDialog(category)}
                  sx={{
                    py: 2,
                    px: 2,
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.04)
                    },
                    cursor: 'pointer'
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 48 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        bgcolor: category.color || theme.palette.primary.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white'
                      }}
                    >
                      <FolderIcon fontSize="small" />
                    </Box>
                  </ListItemIcon>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle2" fontWeight={500}>
                          {category.name}
                        </Typography>
                        {category.isDefault && (
                          <Chip label="Default" size="small" sx={{ height: 20 }} />
                        )}
                        {category.pdfCount > 0 && (
                          <Chip 
                            label={category.pdfCount}
                            size="small"
                            sx={{ height: 20, minWidth: 32 }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      category.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {category.description}
                        </Typography>
                      )
                    }
                  />
                  
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={(e) => handleMenuOpen(e, category)}
                      sx={{ 
                        opacity: 0.7,
                        '&:hover': { opacity: 1 }
                      }}
                    >
                      <MoreIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
      
      {/* Category Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Category' : 'New Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ mb: 3 }}
              autoFocus
            />
            
            <TextField
              fullWidth
              label="Description (optional)"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              multiline
              rows={2}
              sx={{ mb: 3 }}
            />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  onClick={() => setShowColorPicker(!showColorPicker)}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: formData.color,
                    cursor: 'pointer',
                    border: 2,
                    borderColor: 'divider'
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  Click to change color
                </Typography>
              </Box>
              
              {showColorPicker && (
                <Box sx={{ mt: 2 }}>
                  <HexColorPicker 
                    color={formData.color} 
                    onChange={(color) => setFormData({ ...formData, color })}
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                    {['#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0', '#00BCD4'].map(color => (
                      <Box
                        key={color}
                        onClick={() => setFormData({ ...formData, color })}
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: color,
                          cursor: 'pointer',
                          border: 2,
                          borderColor: formData.color === color ? 'text.primary' : 'transparent'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained">
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleOpenDialog(selectedCategory);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        
        {!selectedCategory?.isDefault && (
          <MenuItem
            onClick={() => {
              setDeleteConfirmOpen(true);
              handleMenuClose();
            }}
            sx={{ color: 'error.main' }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
      
      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will remove the category but not the PDFs within it.
          </Alert>
          <Typography>
            Are you sure you want to delete "{selectedCategory?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PDFCategories;